import { Resources } from 'constants/resources';

import { useEffect } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { AxiosError } from 'axios';
import { OrderConversionRequestParams } from 'config/requests/orderConversion';
import { usePermissions } from 'hooks/usePermissions';
import { useGatewayConversionSettings } from 'pages/Monitoring/OrderConversion/OrderConversionList/hooks/useGatewayConversionSettings';
import { useUpdateGatewayConversionSettings } from 'pages/Monitoring/OrderConversion/OrderConversionList/hooks/useUpdateGatewayConversionSettings';
import { OrderConversionListGatewaySettingsAlert } from 'pages/Monitoring/OrderConversion/OrderConversionList/OrderConversionListGateway/components/OrderConversionListGatewaySettingsAlert';
import { OrderConversionListGatewaySettingsForm } from 'pages/Monitoring/OrderConversion/OrderConversionList/OrderConversionListGateway/components/OrderConversionListGatewaySettingsForm';
import { useOrderConversionGatewaySchema } from 'pages/Monitoring/OrderConversion/OrderConversionList/OrderConversionListGateway/hooks/useOrderConversionGatewaySchema';
import { GatewaySettingsFormValues } from 'pages/Monitoring/OrderConversion/OrderConversionList/OrderConversionListGateway/OrderConversionListGatewayCreate/OrderConversionListGatewayCreate';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { queryClient } from 'providers/queryClient';
import { RecordContextProvider, useNotify } from 'react-admin';
import { FormProvider, useForm } from 'react-hook-form';
import { Button, SaveButton } from 'shared/mui/Buttons';
import { CardActionsButtons } from 'shared/mui/Card';
import { CardContent } from 'shared/mui/Card/CardContent/CardContent';
import { CardHeader } from 'shared/mui/Card/CardHeader/CardHeader';
import { Dialog } from 'shared/mui/Dialogs';
import { CloseIconButton, DeleteIconButton } from 'shared/mui/IconButtons';
import { TypographyWithBold } from 'shared/mui/Typography';
import { GatewayConversionListItem } from 'types/embedded/orderConversion';
import { Permission } from 'types/embedded/permission';

type Props = {
  record: GatewayConversionListItem | undefined;
  open: boolean;
  onClose: VoidFunction;
  onDelete: (record: GatewayConversionListItem | undefined) => void;
  params: OrderConversionRequestParams;
  onOpenReferenceDialog: VoidFunction;
};

export const OrderConversionListGatewayEdit: React.FC<Props> = ({
  open,
  record,
  onClose,
  onDelete,
  params,
  onOpenReferenceDialog,
}) => {
  const translate = useTypedTranslate();
  const { hasResourcePermissions } = usePermissions();
  const notify = useNotify();
  const schema = useOrderConversionGatewaySchema();

  const { data } = useGatewayConversionSettings(record?.gatewayConversionSettingId || null);

  const form = useForm<GatewaySettingsFormValues>({
    resolver: yupResolver<any>(schema),
    shouldUnregister: true,
  });

  useEffect(() => {
    if (data) {
      form.reset({
        notify: data?.notify,
        pendingDeviation: data?.pendingDeviation,
        successDeviation: data?.successDeviation,
        cancelDeviation: data?.cancelDeviation,
        minOrdersCount: data?.minOrdersCount,
      });
    }
  }, [data, form.reset]);

  const { updateGatewayConversionSettings } = useUpdateGatewayConversionSettings();

  async function onSubmit(values: GatewaySettingsFormValues) {
    if (!data?.id) {
      return;
    }

    try {
      await updateGatewayConversionSettings.mutateAsync({
        id: data?.id,
        data: {
          successDeviation: values.successDeviation.toString(),
          cancelDeviation: values.cancelDeviation.toString(),
          pendingDeviation: values.pendingDeviation.toString(),
          minOrdersCount: Number(values?.minOrdersCount),
          notify: values.notify,
        },
      });

      await queryClient.refetchQueries({ queryKey: ['gateway_conversion_settings', data?.id] });
      await queryClient.refetchQueries({
        queryKey: [
          Resources.OrderConversion,
          params.period,
          params.merchantId,
          params?.detailed,
          params?.providerIds,
          params?.gatewayIds,
        ],
      });

      onClose();
      notify(
        <TypographyWithBold
          text={translate('app.orderConversion.settingsUpdatedSuccessfully', {
            gateway: record?.name,
          })}
        />,
        { type: 'success' },
      );
    } catch (e) {
      const error = e as AxiosError<{ detail: string }>;

      notify(
        `${translate('app.notification.error')}: ${error?.response?.data?.detail || translate('app.notification.unknownError')}`,
        { type: 'error' },
      );
    }
  }

  return (
    <Dialog onClose={onClose} open={open}>
      <CardHeader
        appendActions={[
          hasResourcePermissions('gateway_conversion_settings', [
            Permission.Full,
            Permission.Delete,
          ]) && (
            <DeleteIconButton
              key="delete-button"
              onClick={() => {
                onDelete(record);
              }}
            />
          ),
          <CloseIconButton key="close-button" onClose={onClose} />,
        ]}
        sx={{ height: 'auto' }}
        title={translate('app.orderConversion.editThresholdSettingsForMonitoring')}
        titleSize="smallBold"
      />
      <CardContent>
        <OrderConversionListGatewaySettingsAlert onClick={onOpenReferenceDialog} />
      </CardContent>
      <RecordContextProvider value={record}>
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <CardContent>
              <OrderConversionListGatewaySettingsForm />
            </CardContent>
            <CardActionsButtons
              rightActionsSlot={[
                <Button
                  key="close-button"
                  label={translate('app.buttons.cancel')}
                  onClick={onClose}
                  variant="outlined"
                />,
                hasResourcePermissions('gateway_conversion_settings', [
                  Permission.Full,
                  Permission.Update,
                ]) && <SaveButton key="save-button" label={translate('app.buttons.save')} />,
              ]}
            />
          </form>
        </FormProvider>
      </RecordContextProvider>
    </Dialog>
  );
};
